/**
 * Schema Grouping — pure helpers that decide when to group by schema,
 * and build fully-populated schema-container folders from a preloaded
 * object list for a database.
 */

import { DatabaseObject, DatabaseObjectType } from '../../database-objects/models/databaseObjectTypes';
import { DatabaseEngineId } from '../../../core/engines/engineTypes';
import { ObjectExplorerGroupingMode } from '../../../store/appStore';
import {
  createSchemaContainerFolderNode,
  createTablesFolderNode,
  createViewsFolderNode,
  createProgrammabilityFolderNode,
  createStoredProceduresFolderNode,
  createFunctionsFolderNode,
  createUserTypesFolderNode,
  createDatabaseObjectNode,
} from '../models/nodeFactories';
import {
  SchemaContainerFolderNode,
  TablesFolderNode,
  ViewsFolderNode,
  ProgrammabilityFolderNode,
  StoredProceduresFolderNode,
  FunctionsFolderNode,
  FolderTreeNode,
} from '../models/nodeTypes';

/**
 * Auto-grouping triggers when the total of grouping-eligible user objects
 * (tables + views + stored procedures + functions) is STRICTLY GREATER than
 * this threshold. Below this, we prefer the flat layout to avoid bloat on
 * small databases.
 */
export const GROUP_OBJECT_COUNT_THRESHOLD = 15;

const GROUPING_TYPES: ReadonlySet<DatabaseObjectType> = new Set([
  'table',
  'view',
  'stored-procedure',
  'function',
]);

function eligibleObjects(objects: DatabaseObject[]): DatabaseObject[] {
  return objects.filter((o) => GROUPING_TYPES.has(o.type) && o.schema);
}

function uniqueNonEmptySchemas(objects: DatabaseObject[]): string[] {
  const set = new Set<string>();
  for (const obj of objects) if (obj.schema) set.add(obj.schema);
  return Array.from(set);
}

export function shouldGroupBySchema(
  mode: ObjectExplorerGroupingMode,
  objects: DatabaseObject[],
): boolean {
  if (mode === 'never') return false;

  const eligible = eligibleObjects(objects);
  if (eligible.length === 0) return false;

  if (mode === 'always') return uniqueNonEmptySchemas(eligible).length >= 1;

  // 'auto'
  if (eligible.length <= GROUP_OBJECT_COUNT_THRESHOLD) return false;
  return uniqueNonEmptySchemas(eligible).length > 1;
}

export interface BuildSchemaContainersArgs {
  objects: DatabaseObject[];
  connectionId: string;
  database: string;
  engine?: DatabaseEngineId;
}

/**
 * Given all preloaded user objects for a database, returns an array of
 * fully-populated schema-container folders. Each container has
 * Tables / Views / Programmability children, which are themselves schema-scoped
 * and pre-populated with the matching object nodes.
 *
 * Sorted alphabetically by schema name (case-insensitive).
 */
export function buildSchemaContainersForDatabase(
  args: BuildSchemaContainersArgs,
): SchemaContainerFolderNode[] {
  const { objects, connectionId, database, engine } = args;

  const eligible = eligibleObjects(objects);
  const schemas = uniqueNonEmptySchemas(eligible).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase()),
  );

  return schemas.map((schema) => {
    const schemaObjects = eligible.filter((o) => o.schema === schema);
    const tables = schemaObjects.filter((o) => o.type === 'table');
    const views = schemaObjects.filter((o) => o.type === 'view');
    const procs = schemaObjects.filter((o) => o.type === 'stored-procedure');
    const fns = schemaObjects.filter((o) => o.type === 'function');

    const container = createSchemaContainerFolderNode({
      connectionId,
      database,
      schema,
      objectCount: schemaObjects.length,
      engine,
    });

    const tablesFolder = populateFolder(
      createTablesFolderNode(connectionId, database, { schema, parentId: container.id }),
      tables,
    ) as TablesFolderNode;

    const viewsFolder = populateFolder(
      createViewsFolderNode(connectionId, database, { schema, parentId: container.id }),
      views,
    ) as ViewsFolderNode;

    const progFolder = createProgrammabilityFolderNode(connectionId, database, {
      schema,
      parentId: container.id,
    }) as ProgrammabilityFolderNode;

    const spFolder = populateFolder(
      createStoredProceduresFolderNode(connectionId, database, {
        schema,
        parentId: progFolder.id,
      }),
      procs,
    ) as StoredProceduresFolderNode;

    const fnFolder = populateFolder(
      createFunctionsFolderNode(connectionId, database, {
        schema,
        parentId: progFolder.id,
      }),
      fns,
    ) as FunctionsFolderNode;

    // SQL Server: keep Programmability -> Types reachable under grouping too.
    // The three leaves stay lazy — `loadObjectFolder` filters the listing by
    // `metadata.schema`, so a schema container only shows its own types.
    const progChildren: FolderTreeNode[] = [spFolder, fnFolder];
    if (engine === 'mssql') {
      progChildren.push(
        createUserTypesFolderNode(connectionId, database, { schema, parentId: progFolder.id }),
      );
    }

    progFolder.children = progChildren;
    container.children = [tablesFolder, viewsFolder, progFolder];

    return container;
  });
}

function populateFolder<T extends { id: string; children?: unknown }>(
  folder: T,
  objects: DatabaseObject[],
): T {
  (folder as { children: unknown }).children = objects.map((obj) =>
    createDatabaseObjectNode(obj, folder.id),
  );
  return folder;
}
